// src/components/riskassessment/utils/cryptoCalculationUtils.js
//
// ARCrypto Risk Assessment — Scoring Engine
// Source: ARCrypto_Risk_Assessment_Blueprint.md (April 2026)
//
// Scoring Model:
//   1. Each question is scored 1–4.
//   2. Each dimension's average is normalized to 0–100.
//   3. Weighted composite: Tolerance 45% + Capacity 30% + Preference 25%.
//   4. Composite maps to one of 5 risk profiles.
//   5. Safety-rail overrides may CAP the profile to protect users.
//   6. Profile maps to a 7-vault allocation matrix (+ cash buffer).

/* ─── Dimension weights (must sum to 1.0) ─────────────────────────────── */
const DIMENSION_WEIGHTS = {
  tolerance: 0.45,
  capacity: 0.30,
  preference: 0.25,
};

/* ─── Profile thresholds (composite score → profile) ──────────────────── */
const PROFILE_THRESHOLDS = [
  { max: 20, type: "Ultra-Conservative" },
  { max: 40, type: "Moderate" },
  { max: 60, type: "Growth-Oriented" },
  { max: 80, type: "Aggressive" },
  { max: 100, type: "Ultra-Aggressive" },
];

/* ─── Profile rank for cap comparisons (lower = more conservative) ────── */
const PROFILE_RANK = {
  "Ultra-Conservative": 0,
  Moderate: 1,
  "Growth-Oriented": 2,
  Aggressive: 3,
  "Ultra-Aggressive": 4,
};

/* ─── Vault Allocation Matrix (% per profile) ─────────────────────────── */
//   Source: blueprint table in section "Vault Allocation by Risk Profile"
//   Each row sums to 100. The 7 vaults + cash buffer.
const VAULT_ALLOCATIONS = {
  "Ultra-Conservative": { leoBTC: 35, leoETH: 25, gemBTC: 15, gemini: 5, scorpioV: 0, gemETH: 0, gemSOL: 0, cash: 20 },
  Moderate:             { leoBTC: 20, leoETH: 15, gemBTC: 20, gemini: 15, scorpioV: 10, gemETH: 0, gemSOL: 0, cash: 20 },
  "Growth-Oriented":    { leoBTC: 10, leoETH: 10, gemBTC: 15, gemini: 15, scorpioV: 20, gemETH: 10, gemSOL: 5, cash: 15 },
  Aggressive:           { leoBTC: 5, leoETH: 5, gemBTC: 10, gemini: 10, scorpioV: 25, gemETH: 20, gemSOL: 15, cash: 10 },
  "Ultra-Aggressive":   { leoBTC: 0, leoETH: 5, gemBTC: 5, gemini: 10, scorpioV: 20, gemETH: 25, gemSOL: 25, cash: 10 },
};

/* ─── Profile descriptions (concise — full prose in the blueprint) ────── */
const PROFILE_META = {
  "Ultra-Conservative": {
    description:
      "Capital preservation above all. Cannot tolerate significant drawdowns. New to crypto or short time horizon.",
    recommendations: [
      "Anchor in Leo BTC Bull Run — sits in cash during uncertain markets, only deploys on confirmed trends",
      "Pair with Leo ETH for ETH momentum exposure with the same patient philosophy",
      "Maintain a 20% cash/stables buffer for psychological safety and rebalancing",
      "Expect drawdowns of -8% to -12% on individual vaults; blended portfolio typically lower",
    ],
    psychologicalInsights: [
      "You value certainty and stability — and that's a valid investment philosophy",
      "Volatility makes you uncomfortable; the Leo strategies are designed to sit out chaos",
      "Patience is your edge — boredom will be a feature, not a bug",
      "Avoid checking the portfolio daily; let the algorithms do their thing",
    ],
  },
  Moderate: {
    description:
      "Willing to accept controlled volatility for steady growth. Some experience, moderate financial capacity.",
    recommendations: [
      "Balance Leo vaults (anchor) with Gemini BTC's 28/28 recovery record for steady growth",
      "Add a 10% intro to Scorpio V for leveraged growth exposure without dominating risk",
      "Keep Gemini ETH and Gemini SOL excluded — their drawdowns exceed your capacity",
      "Maintain 20% cash buffer for stability and rebalancing",
    ],
    psychologicalInsights: [
      "You understand volatility is the price of growth — and you're willing to pay it within reason",
      "You don't panic when things dip but also don't enjoy wild swings",
      "Gemini BTC's controlled losses align with your temperament",
      "Stay disciplined: the small Scorpio V allocation is meaningful exposure without overdoing it",
    ],
  },
  "Growth-Oriented": {
    description:
      "Accepts meaningful drawdowns for above-average returns. Experienced, solid capacity, longer horizon.",
    recommendations: [
      "Make Scorpio V (20%) your primary growth engine — leveraged BTC trends with +786% live track record",
      "Add Gemini ETH (10%) and a small Gemini SOL position (5%) for highest-return exposure",
      "Maintain Leo BTC + Leo ETH (20% combined) as your conservative anchor",
      "15% cash for tactical flexibility; expect -18% to -25% individual vault drawdowns",
    ],
    psychologicalInsights: [
      "You've been through at least one full crypto cycle and held conviction",
      "10–15% monthly drawdowns shouldn't shake you — you understand the recovery pattern",
      "Time horizon is your edge: 6–12 month windows let drawdown-recovery work in your favor",
      "Diversification across 7 vaults insulates you from any single bad month",
    ],
  },
  Aggressive: {
    description:
      "Comfortable with deep drawdowns, seeks high returns. Very experienced, high capacity, multi-year horizon.",
    recommendations: [
      "Scorpio V at 25% — your largest single allocation, leveraging high-conviction BTC trends",
      "Gemini ETH (20%) + Gemini SOL (15%) = 35% allocation to highest-return vaults",
      "Reduce Leo vaults to 5% each — minimal conservative anchor",
      "10% cash reserve; expect 15–20%+ monthly drawdowns as the cost of upside",
    ],
    psychologicalInsights: [
      "Loss makes you analytical, not emotional — that's a rare and valuable trait",
      "You view drawdowns as temporary and recoveries as inevitable for well-designed systems",
      "You're comfortable with leverage and understand the math of asymmetric returns",
      "The path to outsized returns includes outsized volatility — you've made peace with it",
    ],
  },
  "Ultra-Aggressive": {
    description:
      "Seeks maximum returns, embraces extreme volatility. Battle-tested, high capacity, very long horizon.",
    recommendations: [
      "Half your portfolio in Gemini ETH (25%) + Gemini SOL (25%) — the two highest-risk, highest-return vaults",
      "Scorpio V (20%) adds leveraged BTC exposure",
      "Leo BTC excluded entirely — at this level the capital preservation trade-off isn't worth the opportunity cost",
      "10% cash for rebalancing and tactical deployment; expect 25–35% drawdowns in severe corrections",
    ],
    psychologicalInsights: [
      "You're battle-tested — multiple cycles, deep drawdowns survived with conviction intact",
      "You don't just tolerate volatility, you seek it — you understand it's the mechanism of outsized returns",
      "This capital is genuinely money you can have locked in volatile positions for years",
      "Your strongest weapon is patience — let backtested strategies build their live track records",
    ],
  },
};

/* ─── Safety Rail Override Rules ──────────────────────────────────────── */
//   Each rule examines specific answers and may CAP the user's profile.
//   The cap is applied only if the cap profile is MORE CONSERVATIVE than
//   the score-derived profile. Returns array of { rule, message, capTo }.
const evaluateSafetyRails = (answers) => {
  const overrides = [];

  // Rule 1: Q3 (ruin impact) ≤ 2 → cap at Moderate
  if (answers.q3 != null && answers.q3 <= 2) {
    overrides.push({
      rule: "Q3-ruin-impact",
      message:
        "Going to zero would be devastating or major hardship — capped at Moderate to protect financial stability. No exposure to Scorpio V, Gemini ETH, or Gemini SOL.",
      capTo: "Moderate",
    });
  }

  // Rule 2: Q8 (no emergency fund) = 1 → cap at Ultra-Conservative
  if (answers.q8 != null && answers.q8 <= 1) {
    overrides.push({
      rule: "Q8-emergency-fund",
      message:
        "No emergency fund detected — capped at Ultra-Conservative. Build a 6-month cash buffer before taking on meaningful crypto risk.",
      capTo: "Ultra-Conservative",
    });
  }

  // Rule 3: Q6 (allocation) > 30% AND Q9 (employment) = unemployed → Ultra-Conservative
  // Q6 = 3 means 30–50%, Q6 = 4 means >50%. Q9 = 1 means unemployed.
  if (answers.q6 != null && answers.q6 >= 3 && answers.q9 != null && answers.q9 <= 1) {
    overrides.push({
      rule: "Q6+Q9-concentration-income",
      message:
        "Crypto concentration exceeds 30% and no stable income — concentration risk + income risk = capped at Ultra-Conservative.",
      capTo: "Ultra-Conservative",
    });
  }

  // Rule 4: Q7 (time horizon) < 1 year → cap at Moderate
  if (answers.q7 != null && answers.q7 <= 1) {
    overrides.push({
      rule: "Q7-time-horizon",
      message:
        "Investment horizon is under 1 year — capped at Moderate. Short horizons cannot ride out drawdown-recovery cycles.",
      capTo: "Moderate",
    });
  }

  return overrides;
};

/* ─── Pick the most conservative cap from a set of overrides ──────────── */
const mostConservativeCap = (overrides) => {
  if (!overrides.length) return null;
  let lowestRank = Infinity;
  let cap = null;
  overrides.forEach((o) => {
    const rank = PROFILE_RANK[o.capTo];
    if (rank != null && rank < lowestRank) {
      lowestRank = rank;
      cap = o.capTo;
    }
  });
  return cap;
};

/* ─── Map composite score → profile type ──────────────────────────────── */
const scoreToProfile = (score) => {
  for (const t of PROFILE_THRESHOLDS) {
    if (score <= t.max) return t.type;
  }
  return PROFILE_THRESHOLDS[PROFILE_THRESHOLDS.length - 1].type;
};

/* ─── Calculate dimension scores (each 0–100) ─────────────────────────── */
const calculateDimensionScores = (answers, questions) => {
  const dimAccum = {
    tolerance: { sum: 0, count: 0 },
    capacity: { sum: 0, count: 0 },
    preference: { sum: 0, count: 0 },
  };

  questions.forEach((q) => {
    const dim = q.category;
    if (dimAccum[dim] && answers[q.id] != null) {
      dimAccum[dim].sum += answers[q.id];
      dimAccum[dim].count += 1;
    }
  });

  const out = {};
  Object.keys(dimAccum).forEach((dim) => {
    const { sum, count } = dimAccum[dim];
    // Normalize 1–4 average to 0–100 (1→0, 4→100)
    out[dim] = count > 0 ? ((sum / count - 1) / 3) * 100 : 0;
  });
  return out;
};

/* ─── Backward-compat psychological sub-profiles ──────────────────────── */
//   Existing UI components reference psychProfiles by sub-category id.
//   We keep this surface populated so the legacy components don't break.
export const calculatePsychologicalProfiles = (answers, questions) => {
  const psychQuestions = questions.filter((q) => q.category === "tolerance");
  const out = {};
  psychQuestions.forEach((q) => {
    if (answers[q.id] != null) {
      out[q.subCategory] = {
        raw: answers[q.id],
        percentage: ((answers[q.id] - 1) / 3) * 100,
        label: q.subCategory.replace(/_/g, " "),
      };
    }
  });
  return out;
};

/* ─── Mismatch warning helper (Q15 sold at loss + score Growth+) ──────── */
const checkBehaviorMismatch = (answers, finalProfileType) => {
  if (answers.q15 != null && answers.q15 <= 1) {
    const rank = PROFILE_RANK[finalProfileType];
    if (rank >= PROFILE_RANK["Growth-Oriented"]) {
      return {
        warning:
          "Past behavior mismatch: you previously sold during a significant drawdown, but your composite score places you in a higher-risk profile. Past behavior is the strongest predictor of future behavior — consider whether your stated tolerance survives contact with real losses.",
      };
    }
  }
  return null;
};

/* ─── Build legacy portfolioAllocation shape from new vaultAllocation ─── */
//   Keeps existing UI components working until Phase 2 redesign.
const buildLegacyAllocation = (vaultAlloc) => ({
  stablecoins: vaultAlloc.cash,
  vaults: {
    total: 100 - vaultAlloc.cash,
    breakdown: {
      leo: vaultAlloc.leoBTC + vaultAlloc.leoETH,
      gemini:
        vaultAlloc.gemBTC + vaultAlloc.gemini + vaultAlloc.gemETH + vaultAlloc.gemSOL,
      scorpio: vaultAlloc.scorpioV,
    },
  },
});

/* ─── PUBLIC API: calculateResults ────────────────────────────────────── */
export const calculateResults = (answers, questions) => {
  // 1. Dimension scores (each 0–100)
  const dimScores = calculateDimensionScores(answers, questions);

  // 2. Weighted composite score (0–100)
  const weightedScore =
    dimScores.tolerance * DIMENSION_WEIGHTS.tolerance +
    dimScores.capacity * DIMENSION_WEIGHTS.capacity +
    dimScores.preference * DIMENSION_WEIGHTS.preference;
  const composite = Math.round(weightedScore);

  // 3. Score-derived profile
  let profileType = scoreToProfile(composite);
  const scoreDerivedType = profileType;

  // 4. Apply safety-rail overrides
  const overrides = evaluateSafetyRails(answers);
  const cap = mostConservativeCap(overrides);
  if (cap && PROFILE_RANK[cap] < PROFILE_RANK[profileType]) {
    profileType = cap;
  }
  const capApplied = profileType !== scoreDerivedType;

  // 5. Behavior mismatch warning (separate from override)
  const behaviorWarning = checkBehaviorMismatch(answers, profileType);

  // 6. Look up vault allocation
  const vaultAllocation = VAULT_ALLOCATIONS[profileType];

  // 7. Build legacy-compatible portfolio allocation
  const portfolioAllocation = buildLegacyAllocation(vaultAllocation);

  // 8. Compose riskProfile object (existing UI shape)
  const meta = PROFILE_META[profileType];
  const riskProfile = {
    type: profileType,
    description: meta.description,
    recommendations: meta.recommendations,
    psychologicalInsights: meta.psychologicalInsights,
  };

  // 9. Backward-compat scores by old category names
  //    (existing components may key on 'psychological' / 'financial' / etc.)
  const legacyScores = {
    psychological: dimScores.tolerance,
    financial: dimScores.capacity,
    experience: 0, // no longer a separate dimension
    preference: dimScores.preference,
  };

  return {
    // ─── Legacy fields (preserved for existing UI components) ─────────
    totalScore: composite,
    scores: legacyScores,
    psychProfiles: calculatePsychologicalProfiles(answers, questions),
    riskProfile,
    portfolioAllocation,

    // ─── New fields for Phase 2 results page redesign ─────────────────
    composite: {
      tolerance: Math.round(dimScores.tolerance),
      capacity: Math.round(dimScores.capacity),
      preference: Math.round(dimScores.preference),
      weighted: composite,
      profile: profileType,
      scoreDerivedProfile: scoreDerivedType,
      capApplied,
    },
    vaultAllocation,
    safetyOverrides: overrides,
    behaviorWarning,
    weights: DIMENSION_WEIGHTS,
  };
};

const utils = {
  calculatePsychologicalProfiles,
  calculateResults,
};

export default utils;
